import { Injectable, signal } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TradeHttpService } from '../../../transactions/services/trade-http.service';
import { ITradeDocumentModel } from '../interface/i-trade-document.model';
import { GetExtensionFile } from '../../../shared/helpers/utils';

@Injectable({ providedIn: 'root' })
export class OpenTradeComponentService {
  pageTitle = signal('Open a Trade');
  constructor(
    private readonly tradeTransactionsHttpService: TradeHttpService,
  ) {}

  saveTrade(formattedRawData: any, originatorId: string): Observable<any> {
    const documents: ITradeDocumentModel[] =
      (formattedRawData.documents as ITradeDocumentModel[]) || [];
    delete formattedRawData.documents;

    return this.tradeTransactionsHttpService.postTrade(formattedRawData).pipe(
      switchMap((res: any) => {
        if (documents.length === 0) {
          return of(res);
        }

        const tradeId = res.tradeId as string;
        return forkJoin(
          documents.map((doc) =>
            this.tradeTransactionsHttpService.uploadTradeDocument(
              this.prepareFile(doc.file, originatorId, formattedRawData),
              {
                tradeId,
                documentTypeId: doc.documentTypeId,
                documentNo: doc.fileNo,
              },
            ),
          ),
        ).pipe(map(() => res));
      }),
    );
  }

  updateTrade(
    formattedRawData: any,
    acquisitionId: string,
    originatorId: string,
  ): Observable<any> {
    const documents: ITradeDocumentModel[] =
      (formattedRawData.documents as ITradeDocumentModel[]) || [];
    delete formattedRawData.documents;

    return this.tradeTransactionsHttpService
      .updateTrade({ data: formattedRawData, acquisitionId })
      .pipe(
        switchMap((res: any) => {
          if (documents.length === 0) {
            return of(res);
          }

          const tradeId = res.tradeId as string;
          return forkJoin(
            documents.map((doc) =>
              this.tradeTransactionsHttpService.uploadTradeDocument(
                this.prepareFile(doc.file, originatorId, formattedRawData),
                {
                  tradeId,
                  documentTypeId: doc.documentTypeId,
                  documentNo: doc.fileNo,
                },
              ),
            ),
          ).pipe(map(() => res));
        }),
      );
  }

  private prepareFile(
    file: File,
    originatorId: string,
    formattedRawData: any,
  ): File {
    const credebtorId = (formattedRawData.creditorId ||
      formattedRawData.debtorId) as string;
    const etrType = formattedRawData.type;
    if (etrType === 'RSA') {
      throw new Error('RSA should not contain any documents.');
    }

    const name = this.prepareFileName(
      originatorId,
      credebtorId,
      etrType,
      GetExtensionFile(file),
    );
    return new File([file], `${name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
  }

  private prepareFileName(
    originatorId: string,
    credebtorId: string,
    etrType: 'a' | 'b' | 'c' | 'd' | 'f',
    fileType: string,
  ): string {
    return `${originatorId}-${credebtorId}-${etrType}.${fileType}`;
  }
}
